/**
 * Query Error Service - Integrates TanStack Query with global error handling
 * Sets up global error handlers and configures the QueryClient for error management
 */

import { QueryClient } from '@tanstack/react-query';
import { useErrorStore } from '../stores/errorStore';
import { log } from '../utils/errors/logger';

type HttpLikeError = {
  response?: {
    status?: number;
  };
  message?: string;
};

const getStatusCode = (error: unknown): number | undefined => {
  return (error as HttpLikeError)?.response?.status;
};

/**
 * Configure global error handling for TanStack Query
 */
export const setupQueryErrorHandling = (queryClient: QueryClient) => {
  // Global query error handler
  queryClient.getQueryCache().config.onError = (error, query) => {
    void (async () => {
    // Check if error should be suppressed
    const shouldSuppress = query.meta?.suppressGlobalError === true;
    
    if (shouldSuppress) {
      // Still log for debugging, but don't show to user
      log.debug('Suppressed global query error', {
        queryKey: query.queryKey,
        error: error instanceof Error ? error.message : String(error),
        state: query.state.status
      });
      return; // Exit early - don't handle this error globally
    }
    
    const errorStore = useErrorStore.getState();
    
    // Only handle errors that haven't been handled by individual components
    const queryKey = Array.isArray(query.queryKey) ? query.queryKey.join('/') : String(query.queryKey);
    
    log.error('Global query error handler', {
      queryKey: query.queryKey,
      error: error instanceof Error ? error.message : String(error),
      state: query.state.status
    });

    // Check if this is a network/backend error
    const isNetworkError = error instanceof TypeError && 
                          error.message.toLowerCase().includes('fetch');
    
    const status = getStatusCode(error);
    const isAuthError = status === 401;
    const isTimeoutError = status === 504;
    const isServerError = status !== undefined && status >= 500;
    
    // Use smart error classification instead of simple type checking
    if (isAuthError) {
      // Don't show invasive error dialogs for auth issues - handle silently
      log.info('Authentication required, handling token refresh/redirect silently', {
        queryKey: queryKey,
        error: error instanceof Error ? error.message : String(error)
      });
      // Let the auth system handle this through customFetch interceptor
      return;
    } else if (isTimeoutError) {
      errorStore.handleNetworkError(
        new Error('Gateway Timeout: Backend server nicht erreichbar oder überlastet'), 
        `Query: ${queryKey} (504 Gateway Timeout)`
      );
    } else if (isNetworkError) {
      errorStore.handleNetworkError(error, `Query: ${queryKey}`);
    } else if (isServerError) {
      await errorStore.handleApiError(error, `Query: ${queryKey}`);
    } else {
      // Client errors (4xx except 401) - log but don't show invasive dialogs
      log.warn('Client error in query', {
        queryKey: queryKey,
        status,
        error: error instanceof Error ? error.message : String(error)
      });
    }
    })();
  };

  // Global mutation error handler  
  queryClient.getMutationCache().config.onError = (error, variables, context, mutation) => {
    void (async () => {
    const errorStore = useErrorStore.getState();
    
    const mutationKey = mutation.options.mutationKey 
      ? Array.isArray(mutation.options.mutationKey) 
        ? mutation.options.mutationKey.join('/') 
        : String(mutation.options.mutationKey)
      : 'unknown';

    log.error('Global mutation error handler', {
      mutationKey: mutation.options.mutationKey,
      error: error instanceof Error ? error.message : String(error),
      variables
    });

    // Check error type
    const isNetworkError = error instanceof TypeError && 
                          error.message.toLowerCase().includes('fetch');
    
    const status = getStatusCode(error);
    const isAuthError = status === 401;
    const isValidationError = status === 422;
    const isServerError = status !== undefined && status >= 500;
    
    // Use smart error classification for mutations too
    if (isAuthError) {
      // Don't show invasive error dialogs for auth issues in mutations either
      log.info('Authentication required in mutation, handling silently', {
        mutationKey: mutationKey,
        error: error instanceof Error ? error.message : String(error)
      });
      return;
    } else if (isNetworkError) {
      errorStore.handleNetworkError(error, `Mutation: ${mutationKey}`);
    } else if (isServerError) {
      await errorStore.handleApiError(error, `Mutation: ${mutationKey}`);
    } else if (isValidationError) {
      // Validation errors should be handled by individual components
      log.warn('Validation error in mutation', {
        mutationKey: mutationKey,
        error: error instanceof Error ? error.message : String(error)
      });
    } else {
      // Other client errors - log but don't show invasive dialogs
      log.warn('Client error in mutation', {
        mutationKey: mutationKey,
        status,
        error: error instanceof Error ? error.message : String(error)
      });
    }
    })();
  };

  // Success handlers to update backend status
  queryClient.getQueryCache().config.onSuccess = () => {
    const errorStore = useErrorStore.getState();
    // If we had successful queries, backend is likely online
    if (errorStore.backendStatus === 'offline') {
      errorStore.setBackendStatus('online');
      log.info('Backend status updated to online after successful query');
    }
  };

  queryClient.getMutationCache().config.onSuccess = () => {
    const errorStore = useErrorStore.getState();
    // If we had successful mutations, backend is definitely online
    if (errorStore.backendStatus === 'offline') {
      errorStore.setBackendStatus('online');
      log.info('Backend status updated to online after successful mutation');
    }
  };
};

/**
 * Create a pre-configured QueryClient with error handling
 */
export const createEnhancedQueryClient = (): QueryClient => {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 1000 * 60 * 5, // 5 minutes
        gcTime: 1000 * 60 * 10, // 10 minutes
        retry: (failureCount, error: unknown) => {
          const status = getStatusCode(error);
          // Don't retry auth errors
          if (status === 401) {
            return false;
          }
          
          // Don't retry client errors except timeout and rate limit
          if (status !== undefined && status >= 400 && status < 500) {
            return status === 408 || status === 429;
          }
          
          // Retry network errors and server errors up to 3 times
          return failureCount < 3;
        },
        retryDelay: (attemptIndex) => {
          // Exponential backoff with max delay
          return Math.min(1000 * 2 ** attemptIndex, 30000);
        },
        refetchOnWindowFocus: false, // Reduce noise in development
        refetchOnReconnect: 'always', // Always refetch on reconnect
      },
      mutations: {
        retry: (failureCount, error: unknown) => {
          const status = getStatusCode(error);
          // Don't retry client errors
          if (status !== undefined && status >= 400 && status < 500) {
            return false;
          }
          
          // Only retry server errors once for mutations
          if (status !== undefined && status >= 500) {
            return failureCount < 1;
          }
          
          // Retry network errors once
          const isNetworkError = error instanceof TypeError && 
                                error.message.toLowerCase().includes('fetch');
          
          return isNetworkError && failureCount < 1;
        },
        retryDelay: 2000, // 2 second delay for mutation retries
      },
    },
  });

  // Set up global error handling
  setupQueryErrorHandling(queryClient);

  return queryClient;
};

/**
 * Helper to invalidate queries when backend comes back online
 */
export const setupOnlineInvalidation = (queryClient: QueryClient) => {
  // Listen for backend status changes
  let previousStatus: string | null = null;
  
  const checkAndInvalidate = () => {
    const errorStore = useErrorStore.getState();
    const currentStatus = errorStore.backendStatus;
    
    // If backend just came back online, invalidate all queries
    if (previousStatus === 'offline' && currentStatus === 'online') {
      log.info('Backend came back online, invalidating all queries');
      void queryClient.invalidateQueries();
    }
    
    previousStatus = currentStatus;
  };
  
  // Check periodically
  setInterval(() => {
    void checkAndInvalidate();
  }, 1000);
  
  // Also listen to focus events
  window.addEventListener('focus', () => {
    const errorStore = useErrorStore.getState();
    if (errorStore.backendStatus === 'offline') {
      // Trigger a health check
      void errorStore.checkBackendHealth().then((isOnline) => {
        if (isOnline) {
          void queryClient.invalidateQueries();
        }
      });
    }
  });
};
